import React, { useState } from 'react';
import makeStyles from '@mui/styles/makeStyles';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import Collapse from '@mui/material/Collapse';

const FAQS = [
    {
        question: 'Who is IELTS BandBoost for?',
        answer:
            'IELTS BandBoost is designed for serious IELTS aspirants who want to improve their band score through targeted practice and AI-powered feedback. Whether you are stuck at a particular band or preparing for your first attempt, BandBoost helps you identify and fix specific weaknesses.',
    },
    {
        question: 'Does IELTS BandBoost cover all four IELTS sections?',
        answer:
            'Yes, BandBoost covers all four IELTS sections — Listening, Reading, Writing, and Speaking. Each section includes mock tests designed to replicate the real exam environment with detailed AI feedback.',
    },
    {
        question: 'Will IELTS BandBoost tell me my exact band score?',
        answer:
            'BandBoost provides a predicted band score based on AI analysis of your performance across all four sections. While no platform can guarantee exact official scores, our predictions are closely aligned with real IELTS scoring criteria.',
    },
    {
        question: 'Do I need a teacher or coaching with IELTS BandBoost?',
        answer:
            'No, BandBoost is designed to work as a self-study tool. The AI-powered feedback provides detailed, personalized insights that replace the need for a tutor. However, our Premium plan includes 1-on-1 expert coaching for those who want additional guidance.',
    },
    {
        question: 'How is the mock test environment different from other platforms?',
        answer:
            'Our mock tests replicate the exact format, timing, and difficulty of the real IELTS exam. Combined with instant AI feedback on your performance, you get a practice experience that closely mirrors exam day conditions.',
    },
    {
        question: 'Can I access BandBoost on mobile devices?',
        answer:
            'Yes, BandBoost is fully accessible on mobile devices through your web browser. You can practice anytime, anywhere — whether on your phone, tablet, or desktop.',
    },
];

const useStyles = makeStyles(theme => ({
    root: {
        width: '100%',
        padding: '64px 32px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        [theme.breakpoints.down('md')]: {
            padding: '48px 16px',
        },
    },

    // ── Header ──────────────────────────────────────────
    badge: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: '6px',
        marginBottom: '18px',
    },
    badgeStar: {
        color: '#EE634E',
        fontSize: '0.8rem',
    },
    badgeText: {
        color: '#EE634E',
        fontFamily: 'Mulish, sans-serif',
        fontSize: '0.8rem',
        fontWeight: 600,
        letterSpacing: '0.04em',
    },
    heading: {
        color: '#FFFFFF',
        fontFamily: 'Mulish, sans-serif',
        fontWeight: 800,
        fontSize: '2.2rem',
        lineHeight: 1.25,
        textAlign: 'center',
        marginBottom: '14px',
        [theme.breakpoints.down('md')]: {
            fontSize: '1.6rem',
        },
    },
    subtitle: {
        color: '#E6E6E6',
        fontFamily: 'Mulish, sans-serif',
        fontSize: '0.9rem',
        lineHeight: 1.65,
        textAlign: 'center',
        maxWidth: '360px',
        marginBottom: '40px',
    },

    // ── Accordion List ──────────────────────────────────
    list: {
        display: 'flex',
        flexDirection: 'column',
        gap: '10px',
        width: '100%',
    },

    // ── Accordion Item ──────────────────────────────────
    item: {
        backgroundColor: '#333651',
        border: '1px solid rgba(255,255,255,0.07)',
        borderRadius: '10px',
        overflow: 'hidden',
    },
    itemHeader: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '18px 22px',
        cursor: 'pointer',
        userSelect: 'none',
        '&:hover': {
            backgroundColor: 'rgba(255,255,255,0.02)',
        },
    },
    itemQuestion: {
        color: '#FFFFFF',
        fontFamily: 'Mulish, sans-serif',
        fontWeight: 600,
        fontSize: '0.9rem',
        lineHeight: 1.45,
        flex: 1,
        marginRight: '16px',
    },
    itemIcon: {
        color: '#E6E6E6',
        fontSize: '1.2rem',
        flexShrink: 0,
    },
    itemAnswer: {
        padding: '0 22px 18px',
        color: '#E6E6E6',
        fontFamily: 'Mulish, sans-serif',
        fontSize: '0.85rem',
        lineHeight: 1.75,
    },
}));

function FAQItem({ faq }) {
    const classes = useStyles();
    const [open, setOpen] = useState(false);

    return (
        <Box className={classes.item}>
            <Box className={classes.itemHeader} onClick={() => setOpen(prev => !prev)}>
                <Typography className={classes.itemQuestion}>
                    {faq.question}
                </Typography>
                {open
                    ? <RemoveIcon className={classes.itemIcon} />
                    : <AddIcon className={classes.itemIcon} />
                }
            </Box>
            <Collapse in={open}>
                <Typography className={classes.itemAnswer}>
                    {faq.answer}
                </Typography>
            </Collapse>
        </Box>
    );
}

function FAQ() {
    const classes = useStyles();

    return (
        <Box id="faq" className={classes.root}>

            {/* Badge */}
            <Box className={classes.badge}>
                <Typography component="span" className={classes.badgeStar}>✳</Typography>
                <Typography component="span" className={classes.badgeText}>
                    FAQs
                </Typography>
            </Box>

            {/* Heading */}
            <Typography className={classes.heading}>
                Frequently Asked Questions
            </Typography>

            {/* Subtitle */}
            <Typography className={classes.subtitle}>
                Everything you need to know about IELTS BandBoost
            </Typography>

            {/* FAQ items */}
            <Box className={classes.list}>
                {FAQS.map(faq => (
                    <FAQItem key={faq.question} faq={faq} />
                ))}
            </Box>

        </Box>
    );
}

export default FAQ;
